import { secureRandom } from '@/lib/secureRandom';
import { create } from 'zustand';
import apiClient from '../lib/api';
import toast from 'react-hot-toast';

export interface TicketAttachment {
  id: string;
  name: string;
  size: string;
  sizeBytes?: number;
  type: 'image' | 'video' | 'document' | 'other';
  mimeType?: string;
  fileUrl: string;
  previewUrl?: string;
  s3Key?: string;
  uploadedAt: string;
  uploadedByName?: string;
}

export interface TicketAttachment {
  id: string;
  name: string;
  filename?: string;
  size: string;
  sizeBytes?: number;
  type: 'image' | 'video' | 'document' | 'other';
  mimeType?: string;
  fileUrl: string;
  previewUrl?: string;
  s3Key?: string;
  uploadedAt: string;
  uploadedByName?: string;
}

export const isTicketBackupJson = (filename?: string): boolean => {
  if (!filename || typeof filename !== 'string') return false;
  const name = filename.trim().toLowerCase();
  return (
    (name.startsWith('ticket#') && name.endsWith('.json')) ||
    (name.startsWith('ticket-') && name.endsWith('.json')) ||
    (name.startsWith('tb#') && name.endsWith('.json')) ||
    /^ticket#\d+\.json$/i.test(name) ||
    /^tb#\d+\.json$/i.test(name)
  );
};

export interface TicketHistoryItem {
  id: string | number;
  ticketId: string | number;
  fromStatus?: string;
  toStatus?: string;
  changedById?: string;
  changedByName?: string;
  actionType?: string;
  action_type?: string;
  buttonLabel?: string;
  buttonCode?: string;
  eventType?: string;
  agent_id?: number;
  agentName?: string;
  agentEmail?: string;
  agentRole?: string;
  author_name?: string;
  author_email?: string;
  note?: string;
  internal: boolean;
  createdAt: string;
  attachments?: TicketAttachment[];
  meta?: Record<string, any>;
  to?: string;
  cc?: string;
  bcc?: string;
  timeTaken?: string;
}

export interface Ticket {
  id: string | number;
  ticketId: string; // e.g. TB#01141
  ticketNumber?: string;
  tenantId?: string;
  tenantCode?: string;
  tenant_code?: string;
  subject: string;
  description: string;
  type: string;
  category?: string;
  categoryId?: string | number;
  category_id?: string | number;
  assetCategory?: string;
  asset_category?: string;
  assetCategoryId?: string | number;
  asset_category_id?: string | number;
  subcategory?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  impact?: 'low' | 'medium' | 'high' | 'urgent';
  urgency?: 'low' | 'medium' | 'high' | 'urgent';
  status: 'new' | 'open' | 'in-progress' | 'pending' | 'with-user' | 'with-supplier' | 'on-hold' | 'awaiting-approval' | 'with-management' | 'with-hr' | 'resolved' | 'closed' | string;
  teamKey?: string;
  teamName?: string;
  teamEmail?: string;
  team_id_ref?: number;
  agent_id?: number;
  agentId?: number;
  client?: string;
  assetTag?: string;
  assetId?: string | number;
  requesterId?: string;
  requesterName?: string;
  requesterEmail?: string;
  requesterDepartment?: string;
  department?: string;
  requesterDesignation?: string;
  requesterPhone?: string;
  assigneeId?: string;
  assigneeName?: string;
  assigneeEmail?: string;
  assigneeRole?: string;
  slaStart?: string;
  slaBreach?: string;
  slaBreached?: boolean;
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
  resolvedAt?: string;
  firstRespondedAt?: string;
  sla?: any;
  slaTracking?: any;
  resolution?: string;
  additionalAgents?: string[];
  source?: string;
  createdFrom?: string;
  createdBy?: string;
  workflow?: string;
  site?: string;
  slaPolicy?: string;
  sla_policy?: string;
  history?: TicketHistoryItem[];
  attachments?: TicketAttachment[];
  asset?: any;
  assetDetails?: any;
  primaryAsset?: any;
  assets?: any[];
  relatedAssets?: any[];
  primaryAssetId?: string | number;
  primaryAssetCode?: string;
  softwareApplication?: string;
  assetCode?: string;
  parentId?: string | number;
  supplierEmail?: string;
  supplierOrgId?: number | string;
  vendorId?: number | string;
  vendorName?: string;
  vendorCode?: string;
  vendorEmail?: string;
  vendorPhone?: string;
  vendorSlaTerms?: string;
  effectiveVendorName?: string;
  isRead?: boolean;
  is_read?: boolean;
  isFlagged?: boolean;
  is_flagged?: boolean;
  slaHold?: boolean;
  sla_hold?: boolean;
  slaHoldStartedAt?: string;
  sla_hold_started_at?: string;
  slaCumulativeHoldSecs?: number;
  sla_cumulative_hold_secs?: number;
  workflowStateId?: string | number;
  workflowId?: string | number;
  workflowName?: string;
  state_id?: string | number;
  stateName?: string;
  currentStageId?: string | number;
  ticketTypeId?: string | number;
  ticketTypeName?: string;
  tag?: string;
  tagCode?: string;
  tagPrefix?: string;
  tags?: string[];
  teamId?: string;
  priorityId?: string;
  impactId?: string;
  urgencyId?: string;
  channelId?: string;
  subcategoryId?: string;
  stateCode?: string;
  badgeColor?: string;
  originModule?: 'people' | 'assets' | 'support' | 'money' | 'work' | 'growth' | 'admin' | string;
  originRecordId?: string;
  originRecordNumber?: string;
  originRecordType?: string;
  entryChannel?: string;
  raisedById?: string;
  raisedByName?: string;
  departmentQueue?: string;
  isConfidential?: boolean;
  childTicketIds?: (string | number)[];
  firstResponseDueAt?: string;
  resolutionDueAt?: string;
  firstResponseMet?: boolean;
  resolutionMet?: boolean;
  responseBreached?: boolean;
  resolutionBreached?: boolean;
  approvalDecision?: 'approved' | 'rejected' | string;
  approverId?: string;
  subtypeId?: string | number;
  subtype?: string;
  projectReference?: string;
  clientReference?: string;
  startDate?: string;
  dueDate?: string;
  plannedCompletionDate?: string;
  actualCompletionDate?: string;
  meetingDate?: string;
  completionEvidence?: string;
  linkedTicketId?: string | number;
  linkNotes?: string;
  linkDependency?: string;
  isSystemGenerated?: boolean;
  sourceReferenceType?: string;
  ticketTypeCode?: string;
  ticket_type_id?: string | number;
  leaveRequest?: any;
  leaveType?: string;
  totalDays?: number;
}

export interface QueueSummary {
  key: string;
  label: string;
  count: number;
}

export interface TicketTypeNumberingConfig {
  prefix: string; // e.g. "INC#" or "SER#" or "CNG#" or "BUG#" or "ACC#"
  digits: number; // e.g. 5 -> 00001
  sequence?: number; // continuous sequence pointer
}

export interface TicketNumberConfig {
  prefix: string; // fallback / default prefix
  digits: number;
  sequence: number; // Global continuous sequence counter shared across all ticket types
  byType?: Record<string, TicketTypeNumberingConfig>;
}

export const DEFAULT_TYPE_NUMBERING: Record<string, TicketTypeNumberingConfig> = {
  // Support Desk
  incident: { prefix: 'INC#', digits: 5, sequence: 1 },
  fault: { prefix: 'INC#', digits: 5, sequence: 1 },
  'sd-fault': { prefix: 'INC#', digits: 5, sequence: 1 },
  'service-request': { prefix: 'SR#', digits: 5, sequence: 1 },
  'sd-sr': { prefix: 'SR#', digits: 5, sequence: 1 },
  change: { prefix: 'CHG#', digits: 5, sequence: 1 },
  'change-requests': { prefix: 'CHG#', digits: 5, sequence: 1 },
  'sd-chg': { prefix: 'CHG#', digits: 5, sequence: 1 },
  access: { prefix: 'ACC#', digits: 5, sequence: 1 },
  'sd-acc': { prefix: 'ACC#', digits: 5, sequence: 1 },
  info: { prefix: 'INFO#', digits: 5, sequence: 1 },
  'sd-info': { prefix: 'INFO#', digits: 5, sequence: 1 },

  // Technical Desk
  hardware: { prefix: 'HW#', digits: 5, sequence: 1 },
  'hardware-fault': { prefix: 'HW#', digits: 5, sequence: 1 },
  'td-hw': { prefix: 'HW#', digits: 5, sequence: 1 },
  network: { prefix: 'NET#', digits: 5, sequence: 1 },
  'network-issue': { prefix: 'NET#', digits: 5, sequence: 1 },
  'td-net': { prefix: 'NET#', digits: 5, sequence: 1 },
  'td-inc': { prefix: 'INC#', digits: 5, sequence: 1 },
  'td-tchg': { prefix: 'TCHG#', digits: 5, sequence: 1 },
  'td-tsr': { prefix: 'TSR#', digits: 5, sequence: 1 },

  // Software Team
  problem: { prefix: 'BUG#', digits: 5, sequence: 1 },
  bug: { prefix: 'BUG#', digits: 5, sequence: 1 },
  'sw-bug': { prefix: 'BUG#', digits: 5, sequence: 1 },
  development: { prefix: 'DEV#', digits: 5, sequence: 1 },
  'sw-dev': { prefix: 'DEV#', digits: 5, sequence: 1 },
  deployment: { prefix: 'DEP#', digits: 5, sequence: 1 },
  'sw-dep': { prefix: 'DEP#', digits: 5, sequence: 1 },
  enhancement: { prefix: 'ENH#', digits: 5, sequence: 1 },
  'sw-enh': { prefix: 'ENH#', digits: 5, sequence: 1 },
  feature: { prefix: 'FEAT#', digits: 5, sequence: 1 },
  'sw-feat': { prefix: 'FEAT#', digits: 5, sequence: 1 },

  // Supplier Team
  replacement: { prefix: 'RPL#', digits: 5, sequence: 1 },
  'sup-rpl': { prefix: 'RPL#', digits: 5, sequence: 1 },
  rma: { prefix: 'RMA#', digits: 5, sequence: 1 },
  'sup-rma': { prefix: 'RMA#', digits: 5, sequence: 1 },
  'supplier-issue': { prefix: 'SUP#', digits: 5, sequence: 1 },
  'sup-sup': { prefix: 'SUP#', digits: 5, sequence: 1 },
  warranty: { prefix: 'WAR#', digits: 5, sequence: 1 },
  'sup-war': { prefix: 'WAR#', digits: 5, sequence: 1 },

  // HR Team
  leave: { prefix: 'LEAVE#', digits: 5, sequence: 1 },
  'hr-leave': { prefix: 'LEAVE#', digits: 5, sequence: 1 },
  onboarding: { prefix: 'ONB#', digits: 5, sequence: 1 },
  'hr-onb': { prefix: 'ONB#', digits: 5, sequence: 1 },
  offboarding: { prefix: 'OFFB#', digits: 5, sequence: 1 },
  'hr-offb': { prefix: 'OFFB#', digits: 5, sequence: 1 },
  'hr-enquiry': { prefix: 'HRQ#', digits: 5, sequence: 1 },
  'hr-hrq': { prefix: 'HRQ#', digits: 5, sequence: 1 },

  // Management Team
  approval: { prefix: 'APR#', digits: 5, sequence: 1 },
  'mgt-apr': { prefix: 'APR#', digits: 5, sequence: 1 },
  budget: { prefix: 'BUD#', digits: 5, sequence: 1 },
  'mgt-bud': { prefix: 'BUD#', digits: 5, sequence: 1 },
  'major-incident': { prefix: 'MAJ#', digits: 5, sequence: 1 },
  'mgt-maj': { prefix: 'MAJ#', digits: 5, sequence: 1 },
};

export const DEFAULT_TICKET_NUMBER_CONFIG: TicketNumberConfig = {
  prefix: 'INC#',
  digits: 5,
  sequence: 1,
  byType: { ...DEFAULT_TYPE_NUMBERING },
};

export const normalizeTicketTypeKey = (type?: string): string => {
  if (!type) return 'incident';
  const clean = type.toLowerCase().trim().replace(/[\s_\-#]+/g, '-');
  if (DEFAULT_TYPE_NUMBERING[clean]) return clean;
  if (clean.includes('hardware') || clean.includes('hw')) return 'hardware-fault';
  if (clean.includes('network') || clean.includes('net')) return 'network-issue';
  if (clean.includes('leave')) return 'hr-leave';
  if (clean.includes('onboard') || clean.includes('onb')) return 'hr-onb';
  if (clean.includes('offboard') || clean.includes('offb')) return 'hr-offb';
  if (clean.includes('hr') || clean.includes('hrq')) return 'hr-hrq';
  if (clean.includes('budget') || clean.includes('bud')) return 'mgt-bud';
  if (clean.includes('major') || clean.includes('maj')) return 'mgt-maj';
  if (clean.includes('approval') || clean.includes('apr')) return 'mgt-apr';
  if (clean.includes('replacement') || clean.includes('rpl')) return 'sup-rpl';
  if (clean.includes('rma')) return 'sup-rma';
  if (clean.includes('supplier') || clean.includes('sup')) return 'sup-sup';
  if (clean.includes('warranty') || clean.includes('war')) return 'sup-war';
  if (clean.includes('deploy') || clean.includes('dep')) return 'sw-dep';
  if (clean.includes('dev')) return 'sw-dev';
  if (clean.includes('enhanc') || clean.includes('enh')) return 'sw-enh';
  if (clean.includes('feat')) return 'sw-feat';
  if (clean.includes('bug')) return 'sw-bug';
  if (clean.includes('problem') || clean.includes('prb')) return 'problem';
  if (clean.includes('change') || clean.includes('chg') || clean.includes('tchg')) return 'change-requests';
  if (clean.includes('access') || clean.includes('acc')) return 'access';
  if (clean.includes('info')) return 'info';
  if (clean.includes('serv') || clean.includes('req') || clean.includes('tsr')) return 'service-request';
  if (clean.includes('fault') || clean.includes('inc')) return 'incident';
  return clean;
};

export const getStoredNumberConfig = (): TicketNumberConfig => {
  try {
    const raw = localStorage.getItem('tb_ticket_number_config');
    if (raw) {
      const parsed = JSON.parse(raw);
      const globalSeq = typeof parsed.sequence === 'number' ? parsed.sequence : 10;
      return {
        ...DEFAULT_TICKET_NUMBER_CONFIG,
        ...parsed,
        sequence: globalSeq,
        byType: {
          ...DEFAULT_TYPE_NUMBERING,
          ...(parsed.byType || {}),
        },
      };
    }
  } catch (e) {
    // fallback
  }
  return DEFAULT_TICKET_NUMBER_CONFIG;
};

export const saveStoredNumberConfig = (cfg: TicketNumberConfig) => {
  try {
    localStorage.setItem('tb_ticket_number_config', JSON.stringify(cfg));
  } catch (e) {
    // ignore
  }
  // Persist to server support.settings table
  apiClient.put('/support/settings/general/ticket_number_config', { value: cfg }).catch(() => {});
};

export const normalizeTeamLabel = (label?: string): string => {
  if (!label) return 'Support Team';
  let norm = String(label).replace(/\bDesk\b/gi, 'Team').replace(/\bQueue\b/gi, 'Team').trim();
  const lower = norm.toLowerCase();
  if (lower === 'support' || lower.includes('support') || lower === 'sd') return 'Support Team';
  if (lower === 'administration' || lower.includes('admin') || lower === 'adm' || lower.includes('technical')) return 'Administration';
  if (lower === 'software' || lower.includes('software') || lower === 'sw') return 'Software Team';
  if (lower === 'hr' || lower.includes('people') || lower.includes('hr')) return 'HR Team';
  if (lower === 'supplier' || lower.includes('supplier') || lower === 'sup') return 'Supplier Team';
  if (lower === 'management' || lower.includes('management') || lower === 'mgt') return 'Management Team';
  return norm;
};

export const normalizeTeamKey = (key?: string, label?: string): string => {
  const cleanKey = String(key || '').toLowerCase().trim();
  const cleanLabel = String(label || '').toLowerCase().trim();
  if (cleanKey === 'support' || cleanKey === 'sd' || cleanKey === 'support-desk' || cleanLabel.includes('support')) return 'support';
  if (cleanKey === 'administration' || cleanKey === 'admin' || cleanKey === 'adm' || cleanKey === 'technical' || cleanKey === 'technical-desk' || cleanLabel.includes('admin') || cleanLabel.includes('technical')) return 'administration';
  if (cleanKey === 'software' || cleanKey === 'sw' || cleanKey === 'software-team' || cleanLabel.includes('software')) return 'software';
  if (cleanKey === 'hr' || cleanKey === 'hr-team' || cleanKey === 'people' || cleanLabel.includes('hr') || cleanLabel.includes('people')) return 'hr';
  if (cleanKey === 'supplier' || cleanKey === 'sup' || cleanKey === 'supplier-team' || cleanLabel.includes('supplier')) return 'supplier';
  if (cleanKey === 'management' || cleanKey === 'mgt' || cleanKey === 'management-team' || cleanLabel.includes('management')) return 'management';
  return cleanKey || 'support';
};

export const computeQueuesSummary = (ticketsList: Ticket[], existingQueues?: QueueSummary[]): QueueSummary[] => {
  const map: Record<string, { label: string; count: number; order: number }> = {};
  
  // 1. Merge existing queues if provided from API
  if (existingQueues && existingQueues.length > 0) {
    existingQueues.forEach((q, idx) => {
      const k = normalizeTeamKey(q.key, q.label);
      const l = normalizeTeamLabel(q.label);
      if (!map[k]) {
        map[k] = { label: l, count: 0, order: idx + 1 };
      } else {
        map[k].label = l;
      }
    });
  }

  // 2. Count tickets per team
  ticketsList.forEach((t) => {
    const k = normalizeTeamKey(t.teamKey, t.teamName);
    const l = normalizeTeamLabel(t.teamName);
    if (!map[k]) {
      map[k] = { label: l, count: 0, order: 99 };
    }
    const status = String(t.status || '').toLowerCase();
    if (status !== 'resolved' && status !== 'closed') {
      map[k].count += 1;
    }
  });

  const list = Object.entries(map).map(([key, item]) => ({
    key,
    label: item.label,
    count: item.count,
    order: item.order,
  }));

  // Always show Support Team at first position (order 1)
  return list.sort((a, b) => {
    if (a.key === 'support' || a.label === 'Support Team') return -1;
    if (b.key === 'support' || b.label === 'Support Team') return 1;
    return a.order - b.order;
  }).map(({ key, label, count }) => ({ key, label, count }));
};


export const normalizeStatusName = (st?: string): string => {
  if (!st) return 'New';
  const clean = String(st).trim();
  const lower = clean.toLowerCase().replace(/[-_]/g, ' ');
  if (lower === 'new') return 'New';
  if (lower === 'draft') return 'Draft';
  if (lower === 'backlog') return 'Backlog';
  if (lower === 'in progress' || lower === 'inprogress') return 'In Progress';
  if (lower === 'pending') return 'Pending';
  if (lower === 'on hold' || lower === 'onhold' || lower === 'hold') return 'On Hold';
  if (lower === 'awaiting approval' || lower === 'pending approval') return 'Awaiting Approval';
  if (lower === 'with supplier' || lower === 'awaiting supplier') return 'With Supplier';
  if (lower === 'with user' || lower === 'awaiting client' || lower === 'with client') return 'With User';
  if (lower === 'with management') return 'With Management';
  if (lower === 'with hr') return 'With HR';
  if (lower === 'resolved') return 'Resolved';
  if (lower === 'closed') return 'Closed';
  if (lower === 'cancelled' || lower === 'canceled') return 'Cancelled';
  return clean.replace(/\b[a-z]/g, (char) => char.toUpperCase());
};

const mapRawTicket = (item: any): Ticket => {
  const rawSource = String(item.source || item.createdFrom || '').trim();
  let source = 'Manual';
  if (rawSource.toLowerCase().includes('portal')) {
    source = 'Support Portal';
  } else if (rawSource.toLowerCase().includes('email') || rawSource.toLowerCase().includes('mail')) {
    source = 'Email';
  } else if (rawSource.toLowerCase().includes('chat') || rawSource.toLowerCase().includes('bot')) {
    source = 'Chatbot';
  } else if (rawSource) {
    source = rawSource;
  }

  const isAppTriggered = Boolean(
    item.originModule ||
    item.origin_module ||
    item.primaryAssetId ||
    item.primary_asset_id ||
    item.primaryAssetCode ||
    item.primary_asset_code ||
    item.assetId ||
    (item.assetTag && item.assetTag !== '-') ||
    ['assets', 'asset', 'people', 'purchase_order', 'money', 'work'].includes(String(item.source || item.origin_module || '').toLowerCase())
  );

  return {
    id: item.id,
    ticketId: item.ticketNumber || item.ticketId || `TB#${String(item.id).padStart(5, '0')}`,
    ticketNumber: item.ticketNumber || item.ticketId,
    workflowStateId: item.workflowStateId || item.workflow_state_id || item.currentStageId || item.state_id || item.stateId,
    state_id: item.state_id || item.workflowStateId || item.workflow_state_id || item.currentStageId || item.stateId,
    stateName: item.stateName || item.state_name || item.status,
    currentStageId: item.currentStageId || item.current_stage_id || item.workflowStateId || item.state_id,
    workflowId: item.workflowId || item.workflow_id || item.workflow?.id,
    workflowName: item.workflowName || item.workflow_name || (typeof item.workflow === 'string' ? item.workflow : item.workflow?.name),
    ticketTypeId: item.ticketTypeId || item.ticket_type_id,
    ticketTypeName: item.ticketTypeName || item.ticket_type_name || item.type,
    tag: item.tag || item.tagCode || item.tagPrefix || (item.ticketNumber || item.ticketId || '').split('#')[0] || undefined,
    tagCode: item.tagCode || item.tag || item.tagPrefix || (item.ticketNumber || item.ticketId || '').split('#')[0] || undefined,
    tagPrefix: item.tagPrefix || item.tagCode || item.tag || (item.ticketNumber || item.ticketId || '').split('#')[0] || undefined,
    tags: Array.isArray(item.tags) ? item.tags : (item.tag ? [item.tag] : (item.ticketId && item.ticketId.includes('#') ? [item.ticketId.split('#')[0]] : [])),
    teamId: item.teamId || item.team_id,
    priorityId: item.priorityId || item.priority_id,
    stateCode: item.stateCode || item.state_code,
    badgeColor: item.badgeColor || item.badge_color,
    tenantId: item.tenantId || item.tenant_id,
    tenantCode: item.tenantCode || item.tenant_code || '1001',
    tenant_code: item.tenantCode || item.tenant_code || '1001',
    subject: item.subject || 'No Subject',
    description: item.description || '',
    type: item.ticketTypeName || item.ticket_type_name || item.type || 'Incident',
    priority: (item.priority || item.priorityCode || 'low').toLowerCase(),
    impact: (item.impact || item.impactCode || 'low').toLowerCase(),
    urgency: (item.urgency || item.urgencyCode || 'low').toLowerCase(),
    status: normalizeStatusName(item.status || item.stateName || item.state_name || item.stateCode || 'New'),
    category: item.category || 'General',
    subcategory: item.subcategory,
    teamKey: normalizeTeamKey(item.teamKey || item.teamCode || item.teamId, item.teamName || item.team_name),
    teamName: normalizeTeamLabel(item.teamName || item.team_name || 'Support Team'),
    teamEmail: item.teamEmail || item.team_email,
    supplierEmail: item.supplierEmail || item.supplier_email,
    team_id_ref: item.team_id_ref || item.teamIdRef || item.team_id,
    client: item.client || '-',
    assetTag: item.assetTag || item.primary_asset_code || item.primaryAsset?.assetCode || item.asset?.assetTag || item.asset?.assetCode || '-',
    assetId: item.assetId || item.primary_asset_id || item.primaryAsset?.id || item.asset?.id,
    requesterId: item.requesterId,
    requesterName: item.requesterName || item.requester?.name || 'Customer',
    requesterEmail: item.requesterEmail || item.requester?.email,
    requesterDepartment: item.requesterDepartment,
    requesterDesignation: item.requesterDesignation,
    requesterPhone: item.requesterPhone,
    agent_id: item.agent_id || item.agentId || item.assignee?.agent_id || item.assignee?.id,
    agentId: item.agent_id || item.agentId || item.assignee?.agent_id || item.assignee?.id,
    assigneeId: item.assigneeId,
    assigneeName: item.assigneeName || item.assignee?.name || 'Unassigned',
    assigneeEmail: item.assigneeEmail || item.assignee?.email,
    assigneeRole: item.assigneeRole || item.assignee?.role,
    slaStart: item.slaStart || item.createdAt,
    slaBreach: item.slaBreach || item.slaTracking?.breachTime || item.resolutionDueAt || item.resolution_due_at,
    slaBreached: isAppTriggered ? false : Boolean(item.slaBreached || item.sla_breached),
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || new Date().toISOString(),
    closedAt: item.closedAt,
    resolvedAt: item.resolvedAt || item.sla?.resolution?.completedAt,
    firstRespondedAt: item.firstRespondedAt || item.firstResponseAt || item.first_responded_at || item.first_response_at || item.sla?.response?.completedAt || (isAppTriggered ? (item.createdAt || new Date().toISOString()) : undefined),
    sla: item.sla,
    slaTracking: item.slaTracking,
    resolution: item.resolution,
    source,
    workflow: item.workflowName || item.workflow_name || (typeof item.workflow === 'string' ? item.workflow : item.workflow?.name) || 'Incident',
    site: item.site || 'Head Office',
    slaPolicy: item.slaPolicy || item.sla_policy || 'Default SLA Policy',
    sla_policy: item.sla_policy || item.slaPolicy || 'Default SLA Policy',
    additionalAgents: Array.isArray(item.additionalAgents) ? item.additionalAgents : (Array.isArray(item.additional_agents) ? item.additional_agents : []),
    history: Array.isArray(item.history)
      ? item.history.map((h: any) => {
          const rawNote =
            h.note ||
            h.metadata?.comment ||
            h.metadata?.reason ||
            h.metadata?.resolutionNote ||
            (h.newValue?.actionName
              ? `Action "${h.newValue.actionName}" executed. State changed from ${h.oldValue?.stateName || 'previous state'} to ${h.newValue?.stateName || 'new state'}.`
              : (h.eventType === 'ACTION_EXECUTED' || h.eventType === 'STATUS_CHANGED'
                  ? `Status updated to ${h.newValue?.stateName || h.toStatus || 'updated state'}`
                  : (typeof h.newValue === 'string' ? h.newValue : 'Progress action update')));

          const noteStr = String(rawNote || '').trim();
          const noteLower = noteStr.toLowerCase();

          // Resolve specific action done instead of generic "Action Executed"
          let derivedActionType =
            h.buttonLabel ||
            h.button_label ||
            h.actionName ||
            h.action_name ||
            h.actionLabel ||
            h.action_label ||
            h.newValue?.actionName ||
            h.new_value?.actionName ||
            h.actionType ||
            h.action_type;

          if (!derivedActionType || derivedActionType.toLowerCase().includes('action executed') || derivedActionType === 'ACTION_EXECUTED') {
            if (h.buttonLabel || h.button_label) {
              derivedActionType = h.buttonLabel || h.button_label;
            } else if (noteLower.startsWith('executed ')) {
              derivedActionType = noteStr.replace(/^Executed\s+/i, '').trim();
            } else if (noteLower.match(/action\s*["'`]([^"'`]+)["'`]\s*executed/i)) {
              const m = noteStr.match(/action\s*["'`]([^"'`]+)["'`]\s*executed/i);
              if (m && m[1]) derivedActionType = m[1].trim();
            } else if (noteLower.includes('auto-generated ticket') || noteLower.includes('ticket created') || h.fromStageId === null || h.from_stage_id === null || h.eventType === 'TICKET_CREATED') {
              derivedActionType = 'Ticket Created';
            } else if (h.toStatus || h.toStageName || h.newValue?.stateName) {
              derivedActionType = `Status: ${h.toStatus || h.toStageName || h.newValue?.stateName}`;
            } else if (h.eventType && h.eventType !== 'ACTION_EXECUTED') {
              derivedActionType = h.eventType;
            } else {
              derivedActionType = 'Action Executed';
            }
          }

          return {
            id: h.id || `hist_${Date.now()}_${secureRandom()}`,
            ticketId: h.ticketId || item.id || 0,
            fromStatus: h.fromStatus || h.oldValue?.stateName || h.old_value?.stateName || h.fromStageName,
            toStatus: h.toStatus || h.newValue?.stateName || h.new_value?.stateName || h.toStageName,
            changedById: h.changedById || h.userId || h.user_id || h.actorId || h.actor_id,
            changedByName: h.changedByName || h.authorName || h.author_name || 'Staff',
            actionType: derivedActionType,
            action_type: derivedActionType,
            buttonLabel: h.buttonLabel || h.button_label,
            buttonCode: h.buttonCode || h.button_code,
            agent_id: h.agent_id,
            agentName: h.agentName,
            agentEmail: h.agentEmail || h.authorEmail || h.author_email,
            agentRole: h.agentRole,
            author_name: h.author_name || h.authorName || h.changedByName,
            author_email: h.author_email || h.authorEmail,
            note: String(rawNote || '').replace(/\[EMAIL\]/gi, '').trim(),
            internal: Boolean(h.internal !== undefined ? h.internal : (h.metadata?.isInternal ?? true)),
            createdAt: h.createdAt || h.created_at || new Date().toISOString(),
            attachments: Array.isArray(h.attachments)
              ? h.attachments.filter((a: any) => !isTicketBackupJson(a?.name || a?.filename))
              : undefined,
            meta: h.meta || h.metadata,
            to: h.to || h.meta?.to || h.meta?.recipient,
            cc: h.cc || h.meta?.cc,
            bcc: h.bcc || h.meta?.bcc,
            timeTaken: h.timeTaken || h.time_taken || h.meta?.timeTaken || h.meta?.time_taken,
          };
        })
      : (Array.isArray(item.history) ? item.history : []),
    attachments: Array.isArray(item.attachments)
      ? item.attachments.filter((a: any) => !isTicketBackupJson(a?.name || a?.filename))
      : [],
    asset: item.primaryAsset || item.asset || item.assetDetails,
    assetDetails: item.primaryAsset || item.assetDetails || item.asset,
    primaryAsset: item.primaryAsset || item.asset || item.assetDetails,
    assets: Array.isArray(item.assets) ? item.assets : (Array.isArray(item.relatedAssets) ? item.relatedAssets : undefined),
    relatedAssets: Array.isArray(item.relatedAssets) ? item.relatedAssets : (Array.isArray(item.assets) ? item.assets : undefined),
    primaryAssetId: item.primaryAssetId || item.primary_asset_id || item.primaryAsset?.id || item.assetId,
    primaryAssetCode: item.primaryAssetCode || item.primary_asset_code || item.primaryAsset?.assetCode || item.assetTag,
    softwareApplication: item.softwareApplication || item.software_application || item.meta?.softwareApplication || item.meta?.software_application,
    isRead: item.isRead !== undefined ? item.isRead : (item.is_read !== undefined ? item.is_read : true),
    is_read: item.isRead !== undefined ? item.isRead : (item.is_read !== undefined ? item.is_read : true),
    isFlagged: Boolean(item.isFlagged || item.is_flagged),
    is_flagged: Boolean(item.isFlagged || item.is_flagged),
    slaHold: Boolean(item.slaHold || item.sla_hold),
    sla_hold: Boolean(item.slaHold || item.sla_hold),
    slaHoldStartedAt: item.slaHoldStartedAt || item.sla_hold_started_at,
    sla_hold_started_at: item.slaHoldStartedAt || item.sla_hold_started_at,
    slaCumulativeHoldSecs: Number(item.slaCumulativeHoldSecs || item.sla_cumulative_hold_secs || 0),
    sla_cumulative_hold_secs: Number(item.slaCumulativeHoldSecs || item.sla_cumulative_hold_secs || 0),
    originModule: item.originModule || item.origin_module,
    originRecordId: item.originRecordId || item.origin_record_id,
    originRecordNumber: item.originRecordNumber || item.origin_record_number,
    originRecordType: item.originRecordType || item.origin_record_type,
    entryChannel: item.entryChannel || item.entry_channel || item.createdFrom,
    raisedById: item.raisedById || item.raised_by_id,
    raisedByName: item.raisedByName || item.raised_by_name || item.createdBy,
    departmentQueue: item.departmentQueue || item.department_queue || (item.originModule === 'people' ? 'hr' : item.originModule === 'assets' ? 'assets' : 'support'),
    isConfidential: Boolean(item.isConfidential || item.is_confidential),
    childTicketIds: Array.isArray(item.childTicketIds) ? item.childTicketIds : undefined,
    firstResponseDueAt: item.firstResponseDueAt || item.first_response_due_at,
    resolutionDueAt: item.resolutionDueAt || item.resolution_due_at,
    firstResponseMet: item.firstResponseMet !== undefined ? item.firstResponseMet : (item.first_response_met !== undefined ? item.first_response_met : (isAppTriggered ? true : undefined)),
    resolutionMet: item.resolutionMet !== undefined ? item.resolutionMet : item.resolution_met,
    responseBreached: isAppTriggered ? false : (item.responseBreached !== undefined ? item.responseBreached : (item.response_breached !== undefined ? item.response_breached : false)),
    resolutionBreached: item.resolutionBreached !== undefined ? item.resolutionBreached : item.resolution_breached,
    approvalDecision: item.approvalDecision || item.approval_decision,
    approverId: item.approverId || item.approver_id,
  };
};

// Automatic purge of legacy mock caches from earlier versions
if (typeof window !== 'undefined') {
  try {
    const rawQ = localStorage.getItem('teamone_support_queues');
    if (rawQ && (rawQ.includes('Finance Queue') || rawQ.includes('PMO Queue') || rawQ.includes('Admin Queue') || rawQ.includes('Asset / IT Queue'))) {
      localStorage.removeItem('teamone_support_queues');
    }
    const rawT = localStorage.getItem('tb_cached_tickets');
    if (rawT && (
      rawT.includes('tkt-seed-') ||
      rawT.includes('TB#0102') ||
      rawT.includes('Finance Queue') ||
      rawT.includes('Sudharsan DS') ||
      rawT.includes('Rameez Abdul Huq')
    )) {
      localStorage.removeItem('tb_cached_tickets');
    }
    const rawTT = localStorage.getItem('teamone_ticket_types_config');
    if (rawTT && rawTT.includes('tt-incident')) {
      localStorage.removeItem('teamone_ticket_types_config');
    }
    const rawTA = localStorage.getItem('teamone_ticket_actions_config');
    if (rawTA && rawTA.includes('act-accept')) {
      localStorage.removeItem('teamone_ticket_actions_config');
    }
    const rawSla = localStorage.getItem('teamone_support_sla_policies');
    if (rawSla && rawSla.includes('sla-1')) {
      localStorage.removeItem('teamone_support_sla_policies');
    }
  } catch (e) {
    // ignore
  }
}

export const DEFAULT_UNIFIED_SEEDED_TICKETS: Ticket[] = [];

export const getStoredTickets = (): Ticket[] => {
  try {
    const raw = localStorage.getItem('tb_cached_tickets');
    let parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter((t: any) => t && !String(t.id || '').startsWith('tkt-seed-') && !String(t.ticketId || '').startsWith('TB#0102'))
      .map((t: any) => ({
        ...t,
        teamKey: normalizeTeamKey(t.teamKey, t.teamName),
        teamName: normalizeTeamLabel(t.teamName || 'Support Team'),
      }));
  } catch {
    return [];
  }
};

export const saveStoredTickets = (tickets: Ticket[]) => {
  try {
    const cleanTickets = (tickets || []).filter(
      (t: any) => t && !String(t.id || '').startsWith('tkt-seed-') && !String(t.ticketId || '').startsWith('TB#0102')
    );
    localStorage.setItem('tb_cached_tickets', JSON.stringify(cleanTickets));
  } catch (e) {
    // ignore
  }
};

export const isHoldStatus = (st?: string) => {
  const norm = String(st || '').trim().toLowerCase().replace(/[\s_]+/g, '-');
  return (
    norm === 'on-hold' ||
    norm === 'pending' ||
    norm === 'pending-info' ||
    norm === 'with-user' ||
    norm === 'with-supplier' ||
    norm === 'awaiting-approval' ||
    norm === 'with-management' ||
    norm === 'with-hr' ||
    norm.includes('hold') ||
    norm.includes('pending')
  );
};

export const STANDARD_SUPPORT_TEAMS = [
  { key: 'support', label: 'Support Team', order: 1 },
  { key: 'hr', label: 'HR Team', order: 2 },
  { key: 'administration', label: 'Administration', order: 3 },
  { key: 'software', label: 'Software Team', order: 4 },
  { key: 'management', label: 'Management Team', order: 5 },
  { key: 'supplier', label: 'Supplier Team', order: 6 },
];

const initialSeededTickets = getStoredTickets();
const initialQueues = computeQueuesSummary(initialSeededTickets);

interface TicketState {
  tickets: Ticket[];
  currentTicket: Ticket | null;
  queuesSummary: QueueSummary[];
  ticketNumberConfig: TicketNumberConfig;
  isLoading: boolean;
  error: string | null;
  selectedTeamKey: string;
  selectedStatus: string;
  selectedPriority: string;
  selectedType: string;
  searchQuery: string;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  dashboardTickets: Ticket[];
  isDashboardLoading: boolean;
  fetchDashboardTickets: () => Promise<Ticket[]>;

  fetchTickets: (filters?: any, silent?: boolean) => Promise<void>;
  fetchQueuesSummary: () => Promise<void>;
  fetchTicket: (id: string | number) => Promise<Ticket | null>;
  createTicket: (data: Partial<Ticket>) => Promise<Ticket>;
  updateTicket: (id: string | number, data: Partial<Ticket>) => Promise<void>;
  putTicket: (id: string | number, data: Partial<Ticket>) => Promise<void>;
  transitionTicket: (id: string | number, toStatus: string) => Promise<void>;
  addHistory: (
    id: string | number,
    note: string,
    internal?: boolean,
    attachments?: TicketAttachment[],
    actionType?: string,
    authorName?: string
  ) => Promise<void>;
  sendTicketResponse: (
    id: string | number,
    payload: {
      message: string;
      to?: string;
      cc?: string;
      bcc?: string;
      subject?: string;
      sendEmail?: boolean;
      attachments?: TicketAttachment[];
      actionType?: string;
      authorName?: string;
      html?: string;
      timeTaken?: string;
    }
  ) => Promise<void>;
  addComment: (ticketId: string | number, content: string, attachments?: TicketAttachment[]) => Promise<void>;
  deleteTicket: (id: string | number) => Promise<void>;
  assignTicket: (ticketId: string | number, assigneeId: string, assigneeName?: string) => Promise<void>;
  assignAssetToTicket: (ticketId: string | number, asset: any) => Promise<void>;
  unlinkAssetFromTicket: (ticketId: string | number) => Promise<void>;
  syncTicketToVault: (id: string | number) => Promise<any>;
  availableActions: any[];
  fetchAvailableActions: (ticketId: string | number, ticketNumber?: string) => Promise<any[]>;
  executeTicketAction: (ticketId: string | number, actionId: string, payload?: any) => Promise<any>;
  updateTicketNumberConfig: (cfg: Partial<TicketNumberConfig>) => void;
  updateTypeNumberConfig: (typeKey: string, cfg: Partial<TicketTypeNumberingConfig>) => void;
  getNextTicketId: (type?: string) => string;
  setSelectedTeamKey: (teamKey: string) => void;
  setSelectedStatus: (status: string) => void;
  setSelectedPriority: (priority: string) => void;
  setSelectedType: (type: string) => void;
  setSearchQuery: (query: string) => void;
  setPage: (page: number) => void;
  clearCurrentTicket: () => void;
  setCurrentTicket: (ticket: Ticket | null) => void;
  clearError: () => void;
}

export const useTicketStore = create<TicketState>((set, get) => ({
  tickets: initialSeededTickets,
  currentTicket: null,
  availableActions: [],
  queuesSummary: initialQueues,
  ticketNumberConfig: getStoredNumberConfig(),
  isLoading: false,
  error: null,
  selectedTeamKey: 'all',
  selectedStatus: 'all',
  selectedPriority: 'all',
  selectedType: 'all',
  searchQuery: '',
  pagination: {
    page: 1,
    limit: 500,
    total: initialSeededTickets.length,
    totalPages: Math.max(1, Math.ceil(initialSeededTickets.length / 500)),
  },
  dashboardTickets: initialSeededTickets,
  isDashboardLoading: false,

  setSelectedTeamKey: (teamKey: string) => {
    set({ selectedTeamKey: teamKey });
  },

  setSelectedStatus: (status: string) => {
    set({ selectedStatus: status });
  },

  setSelectedPriority: (priority: string) => {
    set({ selectedPriority: priority });
  },

  setSelectedType: (type: string) => {
    set({ selectedType: type });
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  setPage: (page: number) => {
    set((state) => ({ pagination: { ...state.pagination, page } }));
    get().fetchTickets();
  },

  fetchQueuesSummary: async () => {
    try {
      const response = await apiClient.get('/support/tickets/queues-summary');
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : null;
      if (Array.isArray(data) && data.length > 0) {
        const normalized = data.map((q: any) => ({
          key: normalizeTeamKey(q.key || q.code, q.label || q.name),
          label: normalizeTeamLabel(q.label || q.name),
          count: Number(q.count || 0),
        }));

        // Strictly database-driven queue summary
        const sorted = normalized.sort((a, b) => {
          if (a.key === 'support' || a.label === 'Support Team') return -1;
          if (b.key === 'support' || b.label === 'Support Team') return 1;
          const orderA = STANDARD_SUPPORT_TEAMS.find((s) => s.key === a.key)?.order ?? 99;
          const orderB = STANDARD_SUPPORT_TEAMS.find((s) => s.key === b.key)?.order ?? 99;
          return orderA - orderB;
        });

        set({ queuesSummary: sorted });
        return;
      }
    } catch (err) {
      console.warn('Backend queue summaries unreachable, computing from state:', err);
    }
    // Fallback: compute from tickets in store
    set((state) => ({ queuesSummary: computeQueuesSummary(state.tickets, state.queuesSummary) }));
  },

  fetchDashboardTickets: async () => {
    set({ isDashboardLoading: true });
    try {
      const response = await apiClient.get('/support/tickets', {
        params: {
          page: 1,
          limit: 1000,
          pageSize: 1000,
        },
      });
      const resData = response.data;
      const rawList = Array.isArray(resData)
        ? resData
        : Array.isArray(resData?.items)
        ? resData.items
        : Array.isArray(resData?.data)
        ? resData.data
        : [];
      if (Array.isArray(rawList)) {
        const mappedList: Ticket[] = rawList.map(mapRawTicket);
        saveStoredTickets(mappedList);
        set({ dashboardTickets: mappedList, isDashboardLoading: false });
        return mappedList;
      }
    } catch (err) {
      console.warn('Backend fetchDashboardTickets failed, falling back to cached tickets:', err);
    }
    const fallback = getStoredTickets();
    set({ dashboardTickets: fallback, isDashboardLoading: false });
    return fallback;
  },

  fetchTickets: async (overrideFilters?: any, silent = false) => {
    if (!silent && get().tickets.length === 0) {
      set({ isLoading: true, error: null });
    }
    const { selectedTeamKey, selectedStatus, selectedPriority, selectedType, searchQuery, pagination } = get();
    
    const params = overrideFilters || {
      page: pagination.page,
      pageSize: pagination.limit || 500,
      limit: pagination.limit || 500,
      q: searchQuery,
      search: searchQuery,
      teamKey: selectedTeamKey,
      status: selectedStatus,
      priority: selectedPriority,
      type: selectedType,
    };

    try {
      const response = await apiClient.get('/support/tickets', { params });
      const resData = response.data;
      
      const rawList = Array.isArray(resData)
        ? resData
        : Array.isArray(resData?.items)
        ? resData.items
        : Array.isArray(resData?.data)
        ? resData.data
        : [];

      if (Array.isArray(rawList)) {
        const mappedList: Ticket[] = rawList.map(mapRawTicket);
        saveStoredTickets(mappedList);

        const totalCount = typeof resData?.total === 'number' ? resData.total : mappedList.length;
        const totalPages = Math.max(1, Math.ceil(totalCount / (pagination.limit || 500)));

        set({
          tickets: mappedList,
          queuesSummary: computeQueuesSummary(mappedList),
          pagination: {
            ...pagination,
            total: totalCount,
            totalPages,
          },
          isLoading: false,
        });

        get().fetchQueuesSummary();
      } else {
        const fallback = getStoredTickets();
        set({
          tickets: fallback,
          queuesSummary: computeQueuesSummary(fallback),
          isLoading: false,
        });
      }
    } catch (error: any) {
      console.warn('Fetch tickets from backend failed, using last loaded tickets:', error?.message);
      if (!silent) {
        const fallback = getStoredTickets();
        set({
          tickets: fallback,
          queuesSummary: computeQueuesSummary(fallback),
          isLoading: false,
          error: error?.response?.data?.message || error?.response?.data?.error
            || 'Could not reach the ticket service — showing the last loaded tickets.',
        });
      }
    }
  },

  fetchTicket: async (id: string | number) => {
    set({ isLoading: true, error: null });
    
    try {
      const targetId = encodeURIComponent(String(id));
      const [ticketRes, attRes, histRes] = await Promise.allSettled([
        apiClient.get(`/support/tickets/${targetId}`),
        apiClient.get(`/support/storage/attachments?ticketId=${targetId}`),
        apiClient.get(`/support/tickets/${targetId}/history`),
      ]);

      const rawRes = ticketRes.status === 'fulfilled' ? ticketRes.value.data : null;
      const item = rawRes?.data || rawRes;
      let storageAttachments: TicketAttachment[] = [];
      if (attRes.status === 'fulfilled' && Array.isArray(attRes.value.data?.data)) {
        storageAttachments = attRes.value.data.data.map((att: any) => ({
          id: String(att.id),
          name: att.filename || att.name || 'attachment',
          size: att.sizeBytes ? `${Math.round(att.sizeBytes / 1024)} KB` : 'Attached',
          sizeBytes: att.sizeBytes,
          type: att.category || 'document',
          mimeType: att.contentType,
          fileUrl: att.link || att.fileUrl,
          s3Key: att.storageKey,
          uploadedAt: att.createdAt || new Date().toISOString(),
        }));
      }

      let fetchedHistory: any[] = [];
      if (histRes.status === 'fulfilled' && Array.isArray(histRes.value.data?.data)) {
        fetchedHistory = histRes.value.data.data;
      }

      if (item && (item.id || item.ticketNumber || item.ticket_number)) {
        if (fetchedHistory.length > 0) {
          const existingIds = new Set((item.history || []).map((h: any) => String(h.id)));
          const extra = fetchedHistory.filter((h: any) => !existingIds.has(String(h.id)));
          item.history = [...(item.history || []), ...extra];
        }
        const mapped: Ticket = mapRawTicket(item);
        if (storageAttachments.length > 0) {
          const existingIds = new Set((mapped.attachments || []).map((a) => a.id));
          const newAtts = storageAttachments.filter((a) => !existingIds.has(a.id));
          mapped.attachments = [...(mapped.attachments || []), ...newAtts];
        }
        set((state) => ({
          currentTicket: mapped,
          tickets: state.tickets.map((t) =>
            String(t.id) === String(mapped.id) || String(t.ticketId) === String(mapped.ticketId)
              ? { ...t, ...mapped }
              : t
          ),
          isLoading: false,
        }));
        return mapped;
      }
    } catch (error: any) {
      console.warn('Backend fetch single ticket failed, looking in store:', error?.message);
    }

    const localFound = get().tickets.find((t) => String(t.id) === String(id) || t.ticketId === id);
    if (localFound) {
      set({ currentTicket: localFound, isLoading: false });
      return localFound;
    } else {
      set({ isLoading: false, error: 'Ticket not found' });
      return null;
    }
  },

  syncTicketToVault: async (id: string | number) => {
    try {
      const res = await apiClient.post(`/support/storage/sync-ticket/${id}`);
      const data = res.data;
      toast.success(data.message || 'Ticket JSON synced to Vault!');
      await get().fetchTicket(id);
      return data;
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to sync ticket to vault';
      toast.error(msg);
      throw err;
    }
  },

  createTicket: async (data: Partial<Ticket>) => {
    set({ isLoading: true, error: null });

    const payload: Record<string, any> = {
      ticketTypeId: data.ticketTypeId || (data as any).typeId || (data as any).type,
      subtypeId: (data as any).subtypeId || (data as any).subtype,
      teamId: data.teamId || (data as any).teamKey,
      priorityId: data.priorityId || (data as any).priority,
      impactId: data.impactId || (data as any).impact,
      urgencyId: data.urgencyId || (data as any).urgency,
      channelId: data.channelId,
      categoryId: data.categoryId,
      subcategoryId: data.subcategoryId,
      subject: data.subject || 'Untitled Ticket',
      description: data.description || '',
      requesterId: data.requesterId || (data as any).requesterUuid,
      assignedAgentId: data.assigneeId || data.agentId,
      primaryAssetId: (data as any).primaryAssetId || (data as any).assetId || (data as any).primaryAssetUuid,
      assetId: (data as any).primaryAssetId || (data as any).assetId || (data as any).primaryAssetUuid,
      assetTag: (data as any).assetTag || (data as any).assetCode,
      assetCode: (data as any).assetTag || (data as any).assetCode,
      primaryAssetUuid: (data as any).primaryAssetId || (data as any).assetId || (data as any).primaryAssetUuid,
      originModule: (data as any).originModule || (data as any).origin_module,
      source: (data as any).source || 'Web Portal',
      pendingReason: (data as any).pendingReason,
      projectReference: (data as any).projectReference,
      clientReference: (data as any).clientReference,
      startDate: (data as any).startDate,
      dueDate: (data as any).dueDate,
      plannedCompletionDate: (data as any).plannedCompletionDate,
      actualCompletionDate: (data as any).actualCompletionDate,
      meetingDate: (data as any).meetingDate,
      completionEvidence: (data as any).completionEvidence,
      linkedTicketId: (data as any).linkedTicketId,
      linkNotes: (data as any).linkNotes,
      linkDependency: (data as any).linkDependency,
      formValues: (data as any).formValues || (data as any).customFields,
    };

    try {
      const response = await apiClient.post('/support/tickets', payload);
      const serverTicket = response.data?.data || response.data;
      if (serverTicket && (serverTicket.id || serverTicket.ticketNumber)) {
        const mapped = mapRawTicket(serverTicket);
        const updated = [mapped, ...get().tickets];
        saveStoredTickets(updated);
        set({
          tickets: updated,
          currentTicket: mapped,
          queuesSummary: computeQueuesSummary(updated),
          isLoading: false,
        });
        toast.success(`Ticket ${mapped.ticketNumber} created successfully`);
        return mapped;
      }
    } catch (error: any) {
      set({ isLoading: false });
      const msg = error.response?.data?.message || error.message || 'Failed to create ticket';
      toast.error(msg);
      throw error;
    }

    throw new Error('Unexpected response when creating ticket');
  },

  fetchAvailableActions: async (ticketId: string | number, ticketNumber?: string) => {
    try {
      const primaryId = encodeURIComponent(String(ticketId || ticketNumber || ''));
      let workflowActions: any[] = [];

      try {
        const res = await apiClient.get(`/support/tickets/${primaryId}/actions`);
        workflowActions = res.data?.data || [];
        if ((!workflowActions || workflowActions.length === 0) && ticketNumber && ticketNumber !== String(ticketId)) {
          try {
            const res2 = await apiClient.get(`/support/tickets/${encodeURIComponent(ticketNumber)}/actions`);
            if (res2.data?.data && res2.data.data.length > 0) {
              workflowActions = res2.data.data;
            }
          } catch {}
        }
      } catch (err) {
        console.warn('Could not fetch workflow actions from /actions:', err);
      }

      set({ availableActions: workflowActions });
      return workflowActions;
    } catch (err) {
      console.error('Failed to fetch available actions:', err);
      set({ availableActions: [] });
      return [];
    }
  },

  executeTicketAction: async (ticketId: string | number, actionId: string, payload: any = {}) => {
    set({ isLoading: true });
    try {
      const primaryId = encodeURIComponent(String(ticketId));
      const res = await apiClient.post(`/support/tickets/${primaryId}/actions/${actionId}`, payload);

      const updatedTicket = res.data?.ticket || res.data?.data;
      const newlyAvailableActions = res.data?.availableActions || [];
      if (updatedTicket) {
        const mapped = mapRawTicket(updatedTicket);
        const updatedList = get().tickets.map((t) => String(t.id) === String(mapped.id) ? { ...t, ...mapped } : t);
        saveStoredTickets(updatedList);
        set({
          currentTicket: mapped,
          availableActions: newlyAvailableActions.length > 0 ? newlyAvailableActions : get().availableActions,
          tickets: updatedList,
          isLoading: false,
        });
        toast.success(res.data?.message || `Action executed: ${mapped.status}`);
        get().fetchAvailableActions(ticketId).catch(() => {});
        return res.data;
      }
      set({ isLoading: false });
      return res.data;
    } catch (err: any) {
      set({ isLoading: false });
      const msg = err.response?.data?.message || err.message || 'Action execution failed';
      toast.error(msg);
      throw err;
    }
  },

  updateTicket: async (id: string | number, data: Partial<Ticket>) => {
    set({ isLoading: true, error: null });

    const existingTicket = get().tickets.find(
      (t) => String(t.id) === String(id) || String(t.ticketId) === String(id) || t.id === id || t.ticketId === id
    ) || (get().currentTicket && (String(get().currentTicket?.id) === String(id) || String(get().currentTicket?.ticketId) === String(id)) ? get().currentTicket : null);

    const nowIso = new Date().toISOString();
    let ruleHistoryEntry: TicketHistoryItem | null = null;
    const extraPatch: Partial<Ticket> = {};

    if (data.status && existingTicket?.status) {
      const fromStatus = existingTicket.status;
      const toStatus = data.status;
      if (isHoldStatus(toStatus) && !isHoldStatus(fromStatus)) {
        extraPatch.slaHold = true;
        extraPatch.slaHoldStartedAt = nowIso;
        ruleHistoryEntry = {
          id: Date.now() + 1,
          ticketId: Number(id) || 0,
          note: `Matched Rules Changed Status to ${toStatus} - SLA Placed on Hold`,
          internal: true,
          actionType: 'Rule Applied',
          createdAt: nowIso,
          changedByName: 'System',
        };
      } else if (!isHoldStatus(toStatus) && isHoldStatus(fromStatus) && toStatus !== 'closed' && toStatus !== 'resolved') {
        extraPatch.slaHold = false;
        if (existingTicket.slaHoldStartedAt) {
          const holdElapsedMs = Math.max(0, Date.now() - new Date(existingTicket.slaHoldStartedAt).getTime());
          const prevBreach = existingTicket.slaBreach ? new Date(existingTicket.slaBreach).getTime() : Date.now() + 4 * 3600 * 1000;
          extraPatch.slaBreach = new Date(prevBreach + holdElapsedMs).toISOString();
          extraPatch.slaCumulativeHoldSecs = (existingTicket.slaCumulativeHoldSecs || 0) + Math.round(holdElapsedMs / 1000);
        }
        extraPatch.slaHoldStartedAt = undefined;
        ruleHistoryEntry = {
          id: Date.now() + 1,
          ticketId: Number(id) || 0,
          note: 'Matched Rules Released SLA from Hold - SLA Clock Resumed',
          internal: true,
          actionType: 'Rule Applied',
          createdAt: nowIso,
          changedByName: 'System',
        };
      }
    } else if (data.slaHold !== undefined && existingTicket) {
      if (data.slaHold && !existingTicket.slaHold) {
        extraPatch.slaHoldStartedAt = nowIso;
        ruleHistoryEntry = {
          id: Date.now() + 1,
          ticketId: Number(id) || 0,
          note: 'Matched Rules Placed SLA on Hold',
          internal: true,
          actionType: 'Rule Applied',
          createdAt: nowIso,
          changedByName: 'System',
        };
      } else if (!data.slaHold && existingTicket.slaHold) {
        if (existingTicket.slaHoldStartedAt) {
          const holdElapsedMs = Math.max(0, Date.now() - new Date(existingTicket.slaHoldStartedAt).getTime());
          const prevBreach = existingTicket.slaBreach ? new Date(existingTicket.slaBreach).getTime() : Date.now() + 4 * 3600 * 1000;
          extraPatch.slaBreach = new Date(prevBreach + holdElapsedMs).toISOString();
          extraPatch.slaCumulativeHoldSecs = (existingTicket.slaCumulativeHoldSecs || 0) + Math.round(holdElapsedMs / 1000);
        }
        extraPatch.slaHoldStartedAt = undefined;
        ruleHistoryEntry = {
          id: Date.now() + 1,
          ticketId: Number(id) || 0,
          note: 'Matched Rules Released SLA from Hold - SLA Clock Resumed',
          internal: true,
          actionType: 'Rule Applied',
          createdAt: nowIso,
          changedByName: 'System',
        };
      }
    }

    const mergedData = { ...data, ...extraPatch };
    const updatedTickets = get().tickets.map((t) =>
      String(t.id) === String(id) || String(t.ticketId) === String(id) || t.id === id || t.ticketId === id
        ? {
            ...t,
            ...mergedData,
            history: ruleHistoryEntry ? [ruleHistoryEntry, ...(t.history || [])] : t.history,
            updatedAt: nowIso,
          }
        : t
    );
    saveStoredTickets(updatedTickets);

    set((state) => ({
      tickets: updatedTickets,
      queuesSummary: computeQueuesSummary(updatedTickets),
      currentTicket:
        state.currentTicket &&
        (String(state.currentTicket.id) === String(id) ||
          String(state.currentTicket.ticketId) === String(id) ||
          state.currentTicket.id === id ||
          state.currentTicket.ticketId === id)
          ? {
              ...state.currentTicket,
              ...mergedData,
              history: ruleHistoryEntry ? [ruleHistoryEntry, ...(state.currentTicket.history || [])] : state.currentTicket.history,
              updatedAt: nowIso,
            }
          : state.currentTicket,
    }));
    
    try {
      const response = await apiClient.patch(`/support/tickets/${id}`, data);
      const payload = response.data?.data ?? response.data;
      if (payload && payload.id) {
        const mapped = mapRawTicket(payload);
        const refreshedList = get().tickets.map((t) =>
          String(t.id) === String(id) || String(t.ticketId) === String(id) || String(t.ticketNumber) === String(id) || String(t.id) === String(payload.id)
            ? {
                ...t,
                ...mapped,
                site: (mapped.site && mapped.site !== 'Head Office') ? mapped.site : (t.site || mapped.site),
                additionalAgents: (mapped.additionalAgents && mapped.additionalAgents.length > 0) ? mapped.additionalAgents : (t.additionalAgents || []),
                slaPolicy: (mapped.slaPolicy && mapped.slaPolicy !== 'Default SLA Policy') ? mapped.slaPolicy : (t.slaPolicy || mapped.slaPolicy),
              }
            : t
        );
        saveStoredTickets(refreshedList);
        set((state) => ({
          tickets: refreshedList,
          queuesSummary: computeQueuesSummary(refreshedList),
          currentTicket:
            state.currentTicket &&
            (String(state.currentTicket.id) === String(id) ||
              String(state.currentTicket.ticketId) === String(id) ||
              String(state.currentTicket.ticketNumber) === String(id) ||
              String(state.currentTicket.id) === String(payload.id))
              ? {
                  ...state.currentTicket,
                  ...mapped,
                  site: (mapped.site && mapped.site !== 'Head Office') ? mapped.site : (state.currentTicket.site || mapped.site),
                  additionalAgents: (mapped.additionalAgents && mapped.additionalAgents.length > 0) ? mapped.additionalAgents : (state.currentTicket.additionalAgents || []),
                  slaPolicy: (mapped.slaPolicy && mapped.slaPolicy !== 'Default SLA Policy') ? mapped.slaPolicy : (state.currentTicket.slaPolicy || mapped.slaPolicy),
                  history: (mapped.history && mapped.history.length > 0) ? mapped.history : state.currentTicket.history,
                }
              : state.currentTicket,
        }));
      }
      get().fetchQueuesSummary();
    } catch (error: any) {
      console.warn('Backend update ticket offline/failed, kept optimistic state');
    } finally {
      set({ isLoading: false });
    }
  },

  putTicket: async (id: string | number, data: Partial<Ticket>) => {
    set({ isLoading: true, error: null });

    const updatedTickets = get().tickets.map((t) =>
      String(t.id) === String(id) || String(t.ticketId) === String(id) || t.id === id || t.ticketId === id
        ? { ...t, ...data, updatedAt: new Date().toISOString() }
        : t
    );
    saveStoredTickets(updatedTickets);

    set((state) => ({
      tickets: updatedTickets,
      queuesSummary: computeQueuesSummary(updatedTickets),
      currentTicket:
        state.currentTicket &&
        (String(state.currentTicket.id) === String(id) ||
          String(state.currentTicket.ticketId) === String(id) ||
          state.currentTicket.id === id ||
          state.currentTicket.ticketId === id)
          ? { ...state.currentTicket, ...data, updatedAt: new Date().toISOString() }
          : state.currentTicket,
    }));
    
    try {
      const response = await apiClient.put(`/support/tickets/${id}`, data);
      if (response.data && response.data.id) {
        const mapped = mapRawTicket(response.data);
        const refreshedList = get().tickets.map((t) =>
          String(t.id) === String(id) || String(t.ticketId) === String(id)
            ? {
                ...t,
                ...mapped,
                history: (t.history && t.history.length > 0) ? t.history : mapped.history,
                attachments: (t.attachments && t.attachments.length > 0) ? t.attachments : mapped.attachments,
              }
            : t
        );
        saveStoredTickets(refreshedList);
        set((state) => ({
          tickets: refreshedList,
          queuesSummary: computeQueuesSummary(refreshedList),
          currentTicket:
            state.currentTicket &&
            (String(state.currentTicket.id) === String(id) || String(state.currentTicket.ticketId) === String(id))
              ? {
                  ...state.currentTicket,
                  ...mapped,
                  history: (state.currentTicket.history && state.currentTicket.history.length > 0)
                    ? state.currentTicket.history
                    : mapped.history,
                  attachments: (state.currentTicket.attachments && state.currentTicket.attachments.length > 0)
                    ? state.currentTicket.attachments
                    : mapped.attachments,
                }
              : state.currentTicket,
        }));
      }
      get().fetchQueuesSummary();
    } catch (error: any) {
      console.warn('Backend PUT ticket failed, kept optimistic state:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  transitionTicket: async (id: string | number, toStatus: string) => {
    if (!toStatus) return;

    const existingTicket = get().tickets.find(
      (t) => String(t.id) === String(id) || String(t.ticketId) === String(id) || t.id === id || t.ticketId === id
    ) || (get().currentTicket && (String(get().currentTicket?.id) === String(id) || String(get().currentTicket?.ticketId) === String(id)) ? get().currentTicket : null);

    if (existingTicket?.status) {
      const curNorm = String(existingTicket.status).trim().toLowerCase().replace(/[\s_]+/g, '-');
      const targetNorm = String(toStatus).trim().toLowerCase().replace(/[\s_]+/g, '-');
      if (curNorm === targetNorm) {
        return;
      }
    }

    set({ isLoading: true, error: null });

    const nowIso = new Date().toISOString();
    const isClosing = toStatus === 'closed' || toStatus === 'resolved';

    const fromStatus = existingTicket?.status || '';
    const isEnteringHold = isHoldStatus(toStatus) && !isHoldStatus(fromStatus);
    const isLeavingHold = isHoldStatus(fromStatus) && !isHoldStatus(toStatus);

    let newSlaHold = existingTicket?.slaHold;
    let newSlaHoldStartedAt = existingTicket?.slaHoldStartedAt;
    let newSlaBreach = existingTicket?.slaBreach;
    let newCumulative = existingTicket?.slaCumulativeHoldSecs || 0;

    let ruleHistoryEntry: TicketHistoryItem | null = null;

    if (isEnteringHold) {
      newSlaHold = true;
      newSlaHoldStartedAt = nowIso;
      ruleHistoryEntry = {
        id: Date.now() + 1,
        ticketId: Number(id) || 0,
        note: `Matched Rules Changed Status to ${toStatus} - SLA Placed on Hold`,
        internal: true,
        actionType: 'Rule Applied',
        createdAt: nowIso,
        changedByName: 'System',
      };
    } else if (isLeavingHold && !isClosing) {
      newSlaHold = false;
      if (newSlaHoldStartedAt) {
        const holdElapsedMs = Math.max(0, Date.now() - new Date(newSlaHoldStartedAt).getTime());
        const prevBreach = newSlaBreach ? new Date(newSlaBreach).getTime() : Date.now() + 4 * 3600 * 1000;
        newSlaBreach = new Date(prevBreach + holdElapsedMs).toISOString();
        newCumulative += Math.round(holdElapsedMs / 1000);
      }
      newSlaHoldStartedAt = undefined;
      ruleHistoryEntry = {
        id: Date.now() + 1,
        ticketId: Number(id) || 0,
        note: 'Matched Rules Released SLA from Hold - SLA Clock Resumed',
        internal: true,
        actionType: 'Rule Applied',
        createdAt: nowIso,
        changedByName: 'System',
      };
    }

    const updatedTickets = get().tickets.map((t) =>
      String(t.id) === String(id) || String(t.ticketId) === String(id) || t.id === id || t.ticketId === id
        ? {
            ...t,
            status: toStatus as any,
            updatedAt: nowIso,
            closedAt: isClosing ? nowIso : t.closedAt,
            slaHold: newSlaHold !== undefined ? newSlaHold : t.slaHold,
            slaHoldStartedAt: newSlaHoldStartedAt !== undefined ? newSlaHoldStartedAt : t.slaHoldStartedAt,
            slaBreach: newSlaBreach || t.slaBreach,
            slaCumulativeHoldSecs: newCumulative,
            history: ruleHistoryEntry ? [ruleHistoryEntry, ...(t.history || [])] : t.history,
          }
        : t
    );
    saveStoredTickets(updatedTickets);

    set((state) => ({
      tickets: updatedTickets,
      queuesSummary: computeQueuesSummary(updatedTickets),
      currentTicket:
        state.currentTicket &&
        (String(state.currentTicket.id) === String(id) ||
          String(state.currentTicket.ticketId) === String(id) ||
          state.currentTicket.id === id ||
          state.currentTicket.ticketId === id)
          ? {
              ...state.currentTicket,
              status: toStatus as any,
              updatedAt: nowIso,
              closedAt: isClosing ? nowIso : state.currentTicket.closedAt,
              slaHold: newSlaHold !== undefined ? newSlaHold : state.currentTicket.slaHold,
              slaHoldStartedAt: newSlaHoldStartedAt !== undefined ? newSlaHoldStartedAt : state.currentTicket.slaHoldStartedAt,
              slaBreach: newSlaBreach || state.currentTicket.slaBreach,
              slaCumulativeHoldSecs: newCumulative,
              history: ruleHistoryEntry ? [ruleHistoryEntry, ...(state.currentTicket.history || [])] : state.currentTicket.history,
            }
          : state.currentTicket,
    }));

    try {
      const response = await apiClient.post(`/support/tickets/${id}/transition`, { to: toStatus });
      if (response.data && response.data.id) {
        const mapped = mapRawTicket(response.data);
        const refreshedList = get().tickets.map((t) =>
          String(t.id) === String(id) || String(t.ticketId) === String(id)
            ? {
                ...t,
                ...mapped,
                history: (t.history && t.history.length > 0) ? t.history : mapped.history,
                attachments: (t.attachments && t.attachments.length > 0) ? t.attachments : mapped.attachments,
              }
            : t
        );
        saveStoredTickets(refreshedList);
        set((state) => ({
          tickets: refreshedList,
          queuesSummary: computeQueuesSummary(refreshedList),
          currentTicket:
            state.currentTicket &&
            (String(state.currentTicket.id) === String(id) || String(state.currentTicket.ticketId) === String(id))
              ? {
                  ...state.currentTicket,
                  ...mapped,
                  history: (state.currentTicket.history && state.currentTicket.history.length > 0)
                    ? state.currentTicket.history
                    : mapped.history,
                  attachments: (state.currentTicket.attachments && state.currentTicket.attachments.length > 0)
                    ? state.currentTicket.attachments
                    : mapped.attachments,
                }
              : state.currentTicket,
        }));
      }
      get().fetchQueuesSummary();
    } catch (error: any) {
      console.warn('Backend status transition failed, attempting direct patch fallback:', error);
      try {
        const patchRes = await apiClient.patch(`/support/tickets/${id}`, { status: toStatus });
        if (patchRes.data && patchRes.data.id) {
          const mapped = mapRawTicket(patchRes.data);
          const refreshedList = get().tickets.map((t) =>
            String(t.id) === String(id) || String(t.ticketId) === String(id)
              ? {
                  ...t,
                  ...mapped,
                  history: (t.history && t.history.length > 0) ? t.history : mapped.history,
                  attachments: (t.attachments && t.attachments.length > 0) ? t.attachments : mapped.attachments,
                }
              : t
          );
          saveStoredTickets(refreshedList);
          set((state) => ({
            tickets: refreshedList,
            queuesSummary: computeQueuesSummary(refreshedList),
            currentTicket:
              state.currentTicket &&
              (String(state.currentTicket.id) === String(id) || String(state.currentTicket.ticketId) === String(id))
                ? {
                    ...state.currentTicket,
                    ...mapped,
                    history: (state.currentTicket.history && state.currentTicket.history.length > 0)
                      ? state.currentTicket.history
                      : mapped.history,
                    attachments: (state.currentTicket.attachments && state.currentTicket.attachments.length > 0)
                      ? state.currentTicket.attachments
                      : mapped.attachments,
                  }
                : state.currentTicket,
          }));
        }
        get().fetchQueuesSummary();
      } catch (patchErr) {
        console.error('Direct fallback status update also failed:', patchErr);
      }
    } finally {
      set({ isLoading: false });
    }
  },

  addHistory: async (
    id: string | number,
    note: string,
    internal: boolean = true,
    attachments?: TicketAttachment[],
    actionType?: string,
    authorName?: string
  ) => {
    const newEntry: TicketHistoryItem = {
      id: Date.now(),
      ticketId: Number(id) || 0,
      note,
      internal,
      actionType,
      createdAt: new Date().toISOString(),
      changedByName: authorName || 'Girikumaran (You)',
      attachments: attachments && attachments.length > 0 ? attachments : undefined,
    };

    const updatedTickets = get().tickets.map((t) => {
      if (String(t.id) === String(id) || String(t.ticketId) === String(id) || t.id === id || t.ticketId === id) {
        const rawHistory = [newEntry, ...(t.history || [])];
        const seen = new Set<string>();
        const dedupedHistory = rawHistory.filter((item) => {
          const key = `${item.id || ''}|${item.actionType || ''}|${item.note || ''}|${item.createdAt || ''}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        return {
          ...t,
          history: dedupedHistory,
          attachments: attachments && attachments.length > 0 ? [...attachments, ...(t.attachments || [])] : t.attachments,
          updatedAt: new Date().toISOString(),
        };
      }
      return t;
    });
    saveStoredTickets(updatedTickets);

    set((state) => ({
      tickets: updatedTickets,
      currentTicket:
        state.currentTicket &&
        (String(state.currentTicket.id) === String(id) ||
          String(state.currentTicket.ticketId) === String(id) ||
          state.currentTicket.id === id ||
          state.currentTicket.ticketId === id)
          ? {
              ...state.currentTicket,
              history: [
                newEntry,
                ...(state.currentTicket.history || []).filter(
                  (item) => item.id !== newEntry.id && !(item.actionType === newEntry.actionType && item.note === newEntry.note && item.createdAt === newEntry.createdAt)
                ),
              ],
              attachments: attachments && attachments.length > 0 ? [...attachments, ...(state.currentTicket.attachments || [])] : state.currentTicket.attachments,
              updatedAt: new Date().toISOString(),
            }
          : state.currentTicket,
    }));

    try {
      await apiClient.post(`/support/tickets/${id}/history`, {
        note,
        internal,
        attachments,
        actionType,
        authorName: authorName || 'Girikumaran (You)',
        to: newEntry.to,
        cc: newEntry.cc,
        bcc: newEntry.bcc,
        timeTaken: newEntry.timeTaken,
      });
      void get().fetchTicket(id).catch(() => {});
    } catch (error: any) {
      console.warn('Backend add history failed, saved entry in state');
    }
  },

  sendTicketResponse: async (
    id: string | number,
    payload: {
      message: string;
      to?: string;
      cc?: string;
      bcc?: string;
      subject?: string;
      sendEmail?: boolean;
      attachments?: TicketAttachment[];
      actionType?: string;
      authorName?: string;
      html?: string;
      timeTaken?: string;
    }
  ) => {
    const actionLabel = payload.actionType || (payload.sendEmail !== false ? 'Email End User (Faults)' : 'Response');
    const author = payload.authorName || 'Girikumaran (You)';
    const newEntry: TicketHistoryItem = {
      id: Date.now(),
      ticketId: Number(id) || 0,
      note: payload.message,
      internal: false,
      actionType: actionLabel,
      createdAt: new Date().toISOString(),
      changedByName: author,
      attachments: payload.attachments && payload.attachments.length > 0 ? payload.attachments : undefined,
      to: payload.to,
      cc: payload.cc,
      bcc: payload.bcc,
      timeTaken: payload.timeTaken,
      meta: {
        to: payload.to,
        cc: payload.cc,
        bcc: payload.bcc,
        timeTaken: payload.timeTaken,
      },
    };

    const actLower = actionLabel.toLowerCase();
    const isHoldAction = actLower.includes('email end user') || actLower.includes('pending') || actLower.includes('supplier');
    const ruleEntry: TicketHistoryItem | null = isHoldAction
      ? {
          id: Date.now() + 2,
          ticketId: Number(id) || 0,
          note: `Matched Rules Applied Action "${actionLabel}" - SLA Placed on Hold`,
          internal: true,
          actionType: 'Rule Applied',
          createdAt: new Date().toISOString(),
          changedByName: 'System',
        }
      : null;

    const newEntries = ruleEntry ? [ruleEntry, newEntry] : [newEntry];

    const updatedTickets = get().tickets.map((t) => {
      if (String(t.id) === String(id) || String(t.ticketId) === String(id) || t.id === id || t.ticketId === id) {
        const rawHistory = [...newEntries, ...(t.history || [])];
        const seen = new Set<string>();
        const dedupedHistory = rawHistory.filter((item) => {
          const key = `${item.id || ''}|${item.actionType || ''}|${item.note || ''}|${item.createdAt || ''}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        return {
          ...t,
          history: dedupedHistory,
          slaHold: isHoldAction ? true : t.slaHold,
          slaHoldStartedAt: isHoldAction ? (t.slaHoldStartedAt || new Date().toISOString()) : t.slaHoldStartedAt,
          attachments: payload.attachments && payload.attachments.length > 0 ? [...payload.attachments, ...(t.attachments || [])] : t.attachments,
          updatedAt: new Date().toISOString(),
        };
      }
      return t;
    });
    saveStoredTickets(updatedTickets);

    set((state) => ({
      tickets: updatedTickets,
      currentTicket:
        state.currentTicket &&
        (String(state.currentTicket.id) === String(id) ||
          String(state.currentTicket.ticketId) === String(id) ||
          state.currentTicket.id === id ||
          state.currentTicket.ticketId === id)
          ? {
              ...state.currentTicket,
              history: [
                ...newEntries,
                ...(state.currentTicket.history || []).filter(
                  (item) => item.id !== newEntry.id && (ruleEntry ? item.id !== ruleEntry.id : true)
                ),
              ],
              slaHold: isHoldAction ? true : state.currentTicket.slaHold,
              slaHoldStartedAt: isHoldAction ? (state.currentTicket.slaHoldStartedAt || new Date().toISOString()) : state.currentTicket.slaHoldStartedAt,
              attachments: payload.attachments && payload.attachments.length > 0 ? [...payload.attachments, ...(state.currentTicket.attachments || [])] : state.currentTicket.attachments,
              updatedAt: new Date().toISOString(),
            }
          : state.currentTicket,
    }));

    try {
      await apiClient.post(`/support/tickets/${id}/respond`, {
        message: payload.message,
        to: payload.to,
        cc: payload.cc,
        bcc: payload.bcc,
        subject: payload.subject,
        sendEmail: payload.sendEmail !== false,
        actionType: actionLabel,
        authorName: author,
        html: payload.html,
        attachments: payload.attachments,
        timeTaken: payload.timeTaken,
      });
      void get().fetchTicket(id).catch(() => {});
    } catch (error: any) {
      console.warn('Backend respond email dispatch failed:', error?.response?.data || error?.message);
      throw error;
    }
  },

  addComment: async (ticketId: string | number, content: string, attachments?: TicketAttachment[]) => {
    return get().addHistory(ticketId, content, false, attachments);
  },

  deleteTicket: async (id: string | number) => {
    set({ isLoading: true, error: null });

    const updatedTickets = get().tickets.filter((t) => String(t.id) !== String(id) && String(t.ticketId) !== String(id) && t.id !== id && t.ticketId !== id);
    saveStoredTickets(updatedTickets);

    set((state) => ({
      tickets: updatedTickets,
      queuesSummary: computeQueuesSummary(updatedTickets),
      currentTicket:
        state.currentTicket &&
        (String(state.currentTicket.id) === String(id) || String(state.currentTicket.ticketId) === String(id) || state.currentTicket.id === id || state.currentTicket.ticketId === id)
          ? null
          : state.currentTicket,
      isLoading: false,
    }));
    
    try {
      await apiClient.delete(`/support/tickets/${id}`);
    } catch (error: any) {
      console.warn('Backend delete ticket failed, removed from local state');
    }
  },

  assignTicket: async (ticketId: string | number, assigneeId: string, assigneeName?: string) => {
    set({ isLoading: true, error: null });

    const name = assigneeName || assigneeId;
    const updatedTickets = get().tickets.map((t) =>
      String(t.id) === String(ticketId) || String(t.ticketId) === String(ticketId) || t.id === ticketId || t.ticketId === ticketId
        ? { ...t, assigneeId, assigneeName: name, updatedAt: new Date().toISOString() }
        : t
    );
    saveStoredTickets(updatedTickets);

    set((state) => ({
      tickets: updatedTickets,
      queuesSummary: computeQueuesSummary(updatedTickets),
      currentTicket:
        state.currentTicket &&
        (String(state.currentTicket.id) === String(ticketId) ||
          String(state.currentTicket.ticketId) === String(ticketId) ||
          state.currentTicket.id === ticketId ||
          state.currentTicket.ticketId === ticketId)
          ? { ...state.currentTicket, assigneeId, assigneeName: name, updatedAt: new Date().toISOString() }
          : state.currentTicket,
      isLoading: false,
    }));
    
    try {
      const response = await apiClient.patch(`/support/tickets/${ticketId}`, { assigneeId, assigneeName: name });
      if (response.data && response.data.id) {
        const mapped = mapRawTicket(response.data);
        set((state) => ({
          currentTicket:
            state.currentTicket &&
            (String(state.currentTicket.id) === String(ticketId) || String(state.currentTicket.ticketId) === String(ticketId))
              ? {
                  ...state.currentTicket,
                  ...mapped,
                  history: (mapped.history && mapped.history.length > 0) ? mapped.history : state.currentTicket.history,
                }
              : state.currentTicket,
        }));
      }
    } catch (error: any) {
      console.warn('Backend assign ticket failed, kept optimistic state');
    } finally {
      set({ isLoading: false });
    }
  },

  assignAssetToTicket: async (ticketId: string | number, asset: any) => {
    set({ isLoading: true, error: null });

    const patchData = {
      assetId: asset.id,
      assetTag: asset.asset_code || asset.name,
      asset: asset,
      assetDetails: asset,
      relatedAssets: [asset],
      primaryAssetId: asset.id,
    };

    const updatedTickets = get().tickets.map((t) =>
      t.id === ticketId || t.ticketId === ticketId ? { ...t, ...patchData, updatedAt: new Date().toISOString() } : t
    );
    saveStoredTickets(updatedTickets);

    set((state) => ({
      tickets: updatedTickets,
      currentTicket:
        state.currentTicket && (state.currentTicket.id === ticketId || state.currentTicket.ticketId === ticketId)
          ? { ...state.currentTicket, ...patchData, updatedAt: new Date().toISOString() }
          : state.currentTicket,
      isLoading: false,
    }));

    try {
      await apiClient.post(`/support/tickets/${ticketId}/asset`, { assetId: asset.id });
    } catch {
      try {
        await apiClient.patch(`/support/tickets/${ticketId}`, {
          assetId: asset.id,
          assetTag: asset.asset_code || asset.name,
        });
      } catch (err) {
        console.warn('Backend assign asset failed, kept optimistic state');
      }
    }
  },

  unlinkAssetFromTicket: async (ticketId: string | number) => {
    set({ isLoading: true, error: null });

    const patchData = {
      assetId: undefined,
      assetTag: undefined,
      asset: undefined,
      assetDetails: undefined,
      relatedAssets: undefined,
      primaryAssetId: undefined,
    };

    const updatedTickets = get().tickets.map((t) =>
      t.id === ticketId || t.ticketId === ticketId ? { ...t, ...patchData, updatedAt: new Date().toISOString() } : t
    );
    saveStoredTickets(updatedTickets);

    set((state) => ({
      tickets: updatedTickets,
      currentTicket:
        state.currentTicket && (state.currentTicket.id === ticketId || state.currentTicket.ticketId === ticketId)
          ? { ...state.currentTicket, ...patchData, updatedAt: new Date().toISOString() }
          : state.currentTicket,
      isLoading: false,
    }));

    try {
      await apiClient.delete(`/support/tickets/${ticketId}/asset`);
    } catch {
      try {
        await apiClient.patch(`/support/tickets/${ticketId}`, { assetId: null, assetTag: null });
      } catch (err) {
        console.warn('Backend unassign asset failed, kept optimistic state');
      }
    }
  },

  getTicketsForAsset: (assetId?: string | number, assetCode?: string) => {
    const { tickets } = get();
    if (!assetId && !assetCode) return [];
    return tickets.filter((t) => {
      const matchId = assetId && (t.assetId === assetId || String(t.assetId) === String(assetId));
      const matchCode = assetCode && (
        t.assetTag?.toLowerCase() === assetCode.toLowerCase() ||
        t.assetDetails?.asset_code?.toLowerCase() === assetCode.toLowerCase() ||
        t.asset?.asset_code?.toLowerCase() === assetCode.toLowerCase()
      );
      return Boolean(matchId || matchCode);
    });
  },

  updateTicketNumberConfig: (cfg: Partial<TicketNumberConfig>) => {
    const current = get().ticketNumberConfig;
    const nextSeq = cfg.sequence !== undefined ? Number(cfg.sequence) : current.sequence;
    const byType = { ...(current.byType || DEFAULT_TYPE_NUMBERING) };
    if (cfg.sequence !== undefined) {
      Object.keys(byType).forEach((k) => {
        byType[k] = { ...byType[k], sequence: nextSeq };
      });
    }
    const updated: TicketNumberConfig = { ...current, ...cfg, sequence: nextSeq, byType };
    saveStoredNumberConfig(updated);
    set({ ticketNumberConfig: updated });
  },

  updateTypeNumberConfig: (typeKey: string, cfg: Partial<TicketTypeNumberingConfig>) => {
    const current = get().ticketNumberConfig;
    const key = normalizeTicketTypeKey(typeKey);
    const byType = { ...(current.byType || DEFAULT_TYPE_NUMBERING) };
    const currentTypeCfg = byType[key] || { prefix: 'INC#', digits: 5, sequence: current.sequence };
    const nextTypeCfg = { ...currentTypeCfg, ...cfg };
    byType[key] = nextTypeCfg;

    // If sequence is updated, keep the shared continuous sequence counter in sync across all types
    const nextGlobalSeq = cfg.sequence !== undefined ? Number(cfg.sequence) : current.sequence;
    if (cfg.sequence !== undefined) {
      Object.keys(byType).forEach((k) => {
        byType[k] = { ...byType[k], sequence: nextGlobalSeq };
      });
    }

    const updated: TicketNumberConfig = {
      ...current,
      sequence: nextGlobalSeq,
      byType,
    };
    if (key === 'incident') {
      if (cfg.prefix !== undefined) updated.prefix = cfg.prefix;
      if (cfg.digits !== undefined) updated.digits = cfg.digits;
    }
    saveStoredNumberConfig(updated);
    set({ ticketNumberConfig: updated });
  },

  getNextTicketId: (type?: string) => {
    const cfg = get().ticketNumberConfig || DEFAULT_TICKET_NUMBER_CONFIG;
    const key = normalizeTicketTypeKey(type);
    const byType = (cfg && cfg.byType) ? cfg.byType : DEFAULT_TYPE_NUMBERING;
    const typeCfg = byType[key] || byType['incident'] || DEFAULT_TYPE_NUMBERING.incident;

    // Continuous sequential number across all types
    const currentGlobalSeq = (cfg && typeof cfg.sequence === 'number') ? cfg.sequence : 10;
    const digits = typeCfg.digits || (cfg && cfg.digits) || 5;
    const prefix = typeCfg.prefix || (cfg && cfg.prefix) || 'INC#';
    const formatted = `${prefix}${String(currentGlobalSeq).padStart(digits, '0')}`;
    const nextGlobalSeq = currentGlobalSeq + 1;

    // Synchronize the continuous sequence counter across all types
    const nextByType = { ...byType };
    Object.keys(nextByType).forEach((k) => {
      nextByType[k] = { ...nextByType[k], sequence: nextGlobalSeq };
    });

    const nextCfg: TicketNumberConfig = {
      prefix: (cfg && cfg.prefix) || 'INC#',
      digits: (cfg && cfg.digits) || 5,
      sequence: nextGlobalSeq,
      byType: nextByType,
    };
    saveStoredNumberConfig(nextCfg);
    set({ ticketNumberConfig: nextCfg });
    return formatted;
  },

  clearCurrentTicket: () => {
    set({ currentTicket: null });
  },

  setCurrentTicket: (ticket: Ticket | null) => {
    set({ currentTicket: ticket });
  },

  clearError: () => {
    set({ error: null });
  },
}));
